import { Operation } from 'src/common/interfaces/activity-model';
import { CostType, QuantityType } from 'src/enums/cost-setup';
import { meanAllActivities } from './precalculate-time';
import { round, unum } from '@oliasoft-open-source/units';
import { ICostItem } from 'src/common/interfaces/price-book.interfaces';

type CostItemWithQuantity = ICostItem & {
  excessNumber?: number;
  holeVolume?: number | string;
  holeLength?: number | string;
  mudVolume?: boolean;
  surfaceSystem?: number;
  losses?: number;
  dilution?: number;
  extraTimeUnit?: string | null;
  extraTimeValue?: number;
  itemCount?: number;
};

export const getItemTotalCost = (item: CostItemWithQuantity, operations: Operation[] = []) => {
  if (item.type === CostType.DayRate) {
    const checkedOperations = operations.filter((op: Operation) =>
      (item.operations ?? []).includes(String(op.operationId)),
    );
    const totalMeanHours = meanAllActivities(checkedOperations).reduce((sum, hours) => sum + hours, 0);
    const totalMeanDays = round(unum(totalMeanHours, 'd', 'h'), 2);
    const extraDays =
      item.extraTimeUnit === 'hours'
        ? round(unum(Number(item.extraTimeValue) || 0, 'd', 'h'), 2)
        : Number(item.extraTimeValue) || 0;
    return (totalMeanDays + extraDays) * (Number(item.cost) || 0);
  }
  if (item.type !== CostType.Quantity) {
    return Number(item.cost) || 0;
  }
  const unitCost = Number(item.unitCost) || 0;
  const excess = Number(item.excessNumber) || 0;
  const extendedMud = item.mudVolume
    ? (Number(item.surfaceSystem) || 0) + (Number(item.losses) || 0) + (Number(item.dilution) || 0)
    : 0;

  switch (item.quantity) {
    case QuantityType.Length:
      return ((Number(item.length) || 0) + excess) * unitCost;
    case QuantityType.Volume:
      return ((Number(item.volume) || 0) + excess + extendedMud) * unitCost;
    case QuantityType.ItemCount:
      return ((Number(item.itemCount) || 0) + excess) * unitCost;
    case QuantityType.HoleVolume:
      return ((Number(item.holeVolume) || 0) + excess + extendedMud) * unitCost;
    case QuantityType.HoleLength:
      return ((Number(item.holeLength) || 0) + excess) * unitCost;
    default:
      return 0;
  }
};
